import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Camera, Sparkle, Wrench, Lightning, Warning, Clock, Play, X } from "@phosphor-icons/react";

/**
 * JobHistoryTimeline — the "evidence locker".
 *
 * Renders every photo + AI event on a WO in one chronological stream so
 * a PM can prove exactly what happened (and when) on a single screen.
 * Used inside JobDetail as the "History" tab.
 */
function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 16).replace("T", " ");
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

const KIND_STYLES = {
  photo:     { color: "border-slate-400 bg-white",   pill: "bg-slate-900 text-white",    Icon: Camera },
  diagnose:  { color: "border-cyan-500 bg-cyan-50",  pill: "bg-cyan-600 text-white",     Icon: Wrench },
  load_calc: { color: "border-amber-500 bg-amber-50",pill: "bg-amber-600 text-white",    Icon: Lightning },
  triage:    { color: "border-violet-500 bg-violet-50", pill: "bg-violet-600 text-white", Icon: Sparkle },
};

function PhotoCard({ item, onOpen }) {
  const isVideo = item.media_type === "video";
  const roomTag = item.inspection_room || (item.tags || [])[0];
  const kindLabel = item.photo_kind === "before" ? "Before" : item.photo_kind === "after" ? "After" : "Receipt";
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`timeline-photo-${item.file_id || ""}`}
      className="group relative aspect-square border-2 border-[#0f172a] overflow-hidden bg-slate-100">
      {isVideo ? (
        <div className="relative w-full h-full">
          <video src={fileUrl(item.path)} className="w-full h-full object-cover" muted playsInline preload="metadata"/>
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="h-9 w-9 bg-white rounded-full flex items-center justify-center">
              <Play weight="fill" size={20} className="text-[#0f172a] ml-0.5"/>
            </div>
          </div>
        </div>
      ) : (
        <img src={fileUrl(item.path)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
      )}
      <span className={`absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${item.photo_kind === "receipt" ? "bg-emerald-600 text-white" : item.photo_kind === "after" ? "bg-[#00C4CC] text-white" : "bg-[#0f172a] text-white"}`}>
        {kindLabel}
      </span>
      {roomTag && (
        <span className="absolute bottom-1 left-1 right-1 truncate px-1 py-0.5 text-[9px] font-black uppercase bg-white/90 text-[#0f172a] text-center">
          {roomTag}
        </span>
      )}
    </button>
  );
}

function AICard({ item }) {
  const style = KIND_STYLES[item.kind] || KIND_STYLES.diagnose;
  const { Icon } = style;
  const title =
    item.kind === "diagnose" ? "AI Leak Diagnose"
    : item.kind === "load_calc" ? "AI Load-Calc"
    : "AI Triage";
  return (
    <div className={`border-2 ${style.color} p-3 shadow-[3px_3px_0_#0f172a]`} data-testid={`timeline-ai-${item.kind}`}>
      <div className="flex items-center gap-2">
        <div className={`h-8 w-8 flex items-center justify-center ${style.pill}`}>
          <Icon weight="fill" size={18}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-sm uppercase tracking-wide">{title}</div>
          <div className="text-[10px] font-bold uppercase text-slate-500 tracking-widest flex items-center gap-1">
            <Clock weight="fill" size={10}/> {fmtWhen(item.at)}
            {item.confidence && <span className="ml-1 px-1 border border-slate-300 rounded text-[9px]">{item.confidence}</span>}
            {item.had_photo && <span className="ml-1 px-1 border border-slate-300 rounded text-[9px]">photo</span>}
            {item.safety_flag && <span className="ml-1 px-1 bg-red-600 text-white rounded text-[9px] inline-flex items-center gap-0.5"><Warning weight="fill" size={9}/> {item.safety_flag}</span>}
          </div>
        </div>
      </div>
      {item.summary && (
        <div className="mt-2 text-[13px] text-slate-800 leading-snug whitespace-pre-wrap">{item.summary}</div>
      )}
      {(item.parts_list && item.parts_list.length > 0) && (
        <div className="mt-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Suggested parts</div>
          <div className="flex flex-wrap gap-1">
            {item.parts_list.map((p, i) => (
              <span key={i} className="px-1.5 py-0.5 text-[11px] font-bold bg-white border border-slate-300 rounded">
                {p.qty > 1 ? `${p.qty}× ` : ""}{p.item}
              </span>
            ))}
          </div>
        </div>
      )}
      {(item.next_steps && item.next_steps.length > 0) && (
        <ul className="mt-2 space-y-0.5 text-[12px] text-slate-700 list-disc list-inside">
          {item.next_steps.slice(0, 4).map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
      {item.est_cents ? (
        <div className="mt-2 inline-block px-2 py-0.5 text-[11px] font-black uppercase tracking-widest bg-[#0f172a] text-white">
          Est ${Math.round((item.est_cents || 0) / 100)}
        </div>
      ) : null}
    </div>
  );
}

export default function JobHistoryTimeline({ jobId, onOpenPhoto }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all"); // all | photos | ai
  const [viewer, setViewer] = useState(null);

  const handleOpen = onOpenPhoto || setViewer;

  useEffect(() => {
    let live = true;
    setData(null); setErr(null);
    api.get(`/jobs/${jobId}/history-timeline`)
      .then(r => { if (live) setData(r.data); })
      .catch(e => { if (live) setErr(e.response?.data?.detail || e.message || "Failed to load history"); });
    return () => { live = false; };
  }, [jobId]);

  if (err) {
    return <div className="p-4 border-2 border-red-500 bg-red-50 text-sm font-bold text-red-800" data-testid="timeline-error">{String(err)}</div>;
  }
  if (!data) {
    return <div className="p-6 text-center text-sm font-bold text-slate-500" data-testid="timeline-loading">Loading history…</div>;
  }

  const items = (data.items || []).filter(i => {
    if (filter === "photos") return i.kind === "photo";
    if (filter === "ai") return i.kind !== "photo";
    return true;
  });

  const totalCount = (data.items || []).length;

  return (
    <div className="space-y-4" data-testid="job-history-timeline">
      {/* Filter chips + counts — always rendered so a zero-result filter
          doesn't strand the user. */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          ["all", `All (${data.count})`],
          ["photos", `Photos (${data.photo_count})`],
          ["ai", `AI (${data.ai_count})`],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`timeline-filter-${k}`}
            className={`h-8 px-3 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest ${
              filter === k ? "bg-[#0f172a] text-white" : "bg-white text-[#0f172a] hover:bg-slate-50"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {totalCount === 0 ? (
        <div className="p-8 text-center border-2 border-dashed border-slate-300" data-testid="timeline-empty">
          <div className="font-display font-black text-lg uppercase tracking-wide text-slate-400">No history yet</div>
          <div className="mt-1 text-xs font-bold text-slate-500">Upload photos or run an AI diagnose to start the evidence locker.</div>
        </div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center border-2 border-dashed border-slate-300" data-testid="timeline-filter-empty">
          <div className="text-sm font-bold text-slate-500">
            No {filter === "photos" ? "photos" : "AI events"} on this WO yet.
          </div>
          <button onClick={() => setFilter("all")} data-testid="timeline-filter-clear"
            className="mt-2 h-8 px-3 border-2 border-[#0f172a] bg-white font-black uppercase text-[10px] tracking-widest hover:bg-slate-50">
            Show all
          </button>
        </div>
      ) : (
      <div className="relative pl-6 border-l-2 border-[#0f172a]">
        {items.map((it, i) => {
          const isNewDay = i === 0 || (it.at || "").slice(0, 10) !== (items[i - 1].at || "").slice(0, 10);
          return (
            <div key={i} className="relative pb-5">
              {isNewDay && (
                <div className="-ml-6 mb-2 pl-6 py-1 text-[10px] font-black uppercase tracking-widest text-slate-500" data-testid="timeline-day-header">
                  {fmtDay(it.at) || "unknown date"}
                </div>
              )}
              <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-white border-2 border-[#0f172a]"/>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                {fmtWhen(it.at)}
              </div>
              {it.kind === "photo" ? (
                <div className="max-w-[180px]">
                  <PhotoCard item={it} onOpen={handleOpen}/>
                </div>
              ) : (
                <AICard item={it}/>
              )}
            </div>
          );
        })}
      </div>
      )}

      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setViewer(null)} data-testid="timeline-viewer">
          <button type="button" onClick={() => setViewer(null)}
            className="absolute top-4 right-4 h-10 w-10 bg-white rounded-full flex items-center justify-center"
            aria-label="Close">
            <X weight="bold" size={20}/>
          </button>
          {viewer.media_type === "video" ? (
            <video src={fileUrl(viewer.path)} controls autoPlay className="max-h-[90vh] max-w-[92vw]" onClick={e => e.stopPropagation()}/>
          ) : (
            <img src={fileUrl(viewer.path)} alt="" className="max-h-[90vh] max-w-[92vw] object-contain" onClick={e => e.stopPropagation()}/>
          )}
        </div>
      )}
    </div>
  );
}
